import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, MapPin, Lock, AlertTriangle, Search, Loader2, LocateFixed, Plus, Trash2, Music, Users, ChevronLeft, ChevronRight, Check, Repeat, Save, LayoutTemplate, Image as ImageIcon, X, Clock, ListMusic, Wand2, Sparkles, Heart, ShieldCheck, Wallet } from "lucide-react";
import { api, apiError, getGeo, LEVELS, money } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import EventRepertoire from "@/components/EventRepertoire";
import TeamBuilder from "@/components/TeamBuilder";
import MapPicker from "@/components/MapPicker";
import ExtraEventPix from "@/components/ExtraEventPix";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const INSTRUMENTS = ["Voz", "Guitarra", "Violão", "Cavaco", "Baixo", "Bateria", "Teclado", "Saxofone", "Trompete", "Violino", "Percussão", "Pandeiro", "DJ"];
const EVENT_TYPES = ["Show", "Ensaio", "Festival", "Culto", "Casamento", "Bar/Restaurante", "Corporativo", "Aniversário", "Outro"];
const GENRES = ["MPB", "Pop/Rock", "Sertanejo", "Gospel", "Samba/Pagode", "Jazz/Blues", "Forró", "Eletrônica", "Clássica"];
const STEPS = ["Dados básicos", "Vagas & cachês", "Repertório", "Regras & publicação"];
const PREVIEW_IMG = "https://images.unsplash.com/photo-1512573314-17727dcdf45c?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function CreateEvent() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editMode = !!id;
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [recurrence, setRecurrence] = useState({ freq: "none", count: 4 });
  const [cep, setCep] = useState("");
  const [geoBusy, setGeoBusy] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const sugTimer = useRef(null);
  const [f, setF] = useState({ name: "", type: "", image: "", apply_open: "", apply_deadline: "", start_time: "", end_time: "", address: "", cache: 0, slots: 1, requirements: "", notes: "", confirm_deadline_min: 60, checkin_radius_m: 500, checkin_lead_min: 60, invite_deadline_min: 1440, sound_check_enabled: false, sound_check_time: "", sound_check_lead_min: 0, payment_mode: "protected" });
  const [genres, setGenres] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [levels, setLevels] = useState({});
  const [roles, setRoles] = useState([{ instrument: "", quantity: 1, cache: "", min_level: "" }]);
  const [minCache, setMinCache] = useState({});
  const [repertoire, setRepertoire] = useState([]);
  const [repMode, setRepMode] = useState("later");
  const [cacheSug, setCacheSug] = useState({});
  const [teamOpen, setTeamOpen] = useState(false);
  const [selectedInvites, setSelectedInvites] = useState([]);
  const [tplChoiceOpen, setTplChoiceOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");

  useEffect(() => {
    if (!editMode) return;
    (async () => {
      try {
        const { data } = await api.get(`/events/${id}`);
        if (!data.is_creator) { setBlocked("Você não tem permissão para editar este evento."); setReady(true); return; }
        if (!data.can_edit) { setBlocked(`Edição indisponível. Só é permitido editar até 1 hora antes do início (até ${data.edit_deadline || "1h antes"}).`); setReady(true); return; }
        setHasConfirmed(!!data.has_confirmed);
        setF({
          name: data.name || "", type: data.type || "", image: data.image || "", apply_open: data.apply_open || "", apply_deadline: data.apply_deadline || "", start_time: data.start_time || "", end_time: data.end_time || "", address: data.address || "",
          cache: data.cache ?? 0, slots: data.slots ?? 1, requirements: data.requirements || "", notes: data.notes || "",
          confirm_deadline_min: data.confirm_deadline_min ?? 60, checkin_radius_m: data.checkin_radius_m ?? 500,
          checkin_lead_min: data.checkin_lead_min ?? 60, invite_deadline_min: data.invite_deadline_min ?? 1440,
          sound_check_enabled: !!data.sound_check_enabled, sound_check_time: data.sound_check_time || "", sound_check_lead_min: data.sound_check_lead_min ?? 0,
        });
        if (data.date) setDate(new Date(data.date + "T00:00:00"));
        if (data.end_date) setEndDate(new Date(data.end_date + "T00:00:00"));
        if (data.coords) setCoords(data.coords);
        setInstruments(data.instruments || []);
        setGenres(data.genres || []);
        setLevels(data.required_levels || {});
        setRoles((data.roles || []).length
          ? data.roles.map((r) => ({ instrument: r.instrument, quantity: r.quantity, cache: r.cache ?? "", min_level: r.min_level || "" }))
          : [{ instrument: "", quantity: 1, cache: "", min_level: "" }]);
        setRepertoire(data.repertoire || []);
        setRepMode((data.repertoire || []).length ? "now" : "later");
        setReady(true);
      } catch (err) { setBlocked(apiError(err.response?.data?.detail) || "Não foi possível carregar o evento."); setReady(true); }
    })();
  }, [id, editMode]);

  useEffect(() => {
    if (editMode) return;
    api.get("/me/plan").then(({ data }) => {
      if (!data.can_create) {
        setLimitInfo({
          message: "Você atingiu o limite de eventos disponível para sua conta.",
          plan: data.plan, used: data.used, limit: data.effective_limit ?? data.limit,
          upgrade_options: data.upgrade_options || [], event_extra_fee: data.event_extra_fee,
        });
      }
      setReady(true);
    }).catch(() => setReady(true));
  }, [editMode]);

  useEffect(() => {
    api.get("/config/min-cache").then(({ data }) => setMinCache(data.items || {})).catch(() => {});
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleInstr = (i) => { if (hasConfirmed) return; setInstruments((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i]); };
  const toggleGenre = (g) => setGenres((p) => p.includes(g) ? p.filter((x) => x !== g) : [...p, g]);
  const onCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await resizeImage(file, 1024, 0.82); setF((p) => ({ ...p, image: url })); }
    catch { toast.error("Não foi possível processar a imagem."); }
    e.target.value = "";
  };
  const addRole = () => { if (hasConfirmed) return; setRoles((p) => [...p, { instrument: "", quantity: 1, cache: "", min_level: "" }]); };
  const removeRole = (idx) => { if (hasConfirmed) return; setRoles((p) => p.filter((_, i) => i !== idx)); };
  const setRole = (idx, k, v) => { if (hasConfirmed) return; setRoles((p) => p.map((r, i) => (i === idx ? { ...r, [k]: v } : r))); };
  const rolesTotal = roles.reduce((a, r) => a + (parseInt(r.quantity) || 0) * (parseFloat(r.cache) || 0), 0);
  const totalSlots = roles.reduce((a, r) => a + (r.instrument ? (parseInt(r.quantity) || 0) : 0), 0);
  const instrList = [...new Set(roles.filter((r) => r.instrument).map((r) => r.instrument))];
  const roleCacheError = (r) => {
    if (!r.instrument) return null;
    const c = parseFloat(r.cache);
    if (r.cache === "" || r.cache === null || isNaN(c) || c <= 0) return "Informe o cachê (maior que R$ 0,00).";
    const mn = minCache[r.instrument];
    if (mn != null && c < mn) return `Abaixo do mínimo permitido: ${money(mn)}.`;
    return null;
  };
  const hasCacheErrors = roles.some((r) => r.instrument && roleCacheError(r));
  const suggestCache = async (idx) => {
    const inst = roles[idx]?.instrument; if (!inst) return;
    try {
      const { data } = await api.get("/cache-suggestion", { params: { instrument: inst } });
      setCacheSug((p) => ({ ...p, [inst]: data }));
      if (data.suggested) setRole(idx, "cache", String(data.suggested));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const inviteMyTeam = async () => {
    try {
      const { data } = await api.get("/team/musicians");
      const primaries = (data || []).filter((m) => m.is_primary);
      const pick = primaries.length ? primaries : (data || []);
      if (!pick.length) return toast.error("Sua equipe está vazia. Salve músicos em \"Minha Equipe\".");
      setSelectedInvites((prev) => {
        const ids = new Set(prev.map((x) => x.user_id));
        const merged = [...prev];
        pick.forEach((m) => { if (!ids.has(m.user_id)) merged.push({ user_id: m.user_id, stage_name: m.stage_name }); });
        return merged;
      });
      toast.success(`${pick.length} músico(s) da sua equipe ${primaries.length ? "principal " : ""}adicionados aos convites.`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const useMyLocation = async () => {
    setGeoBusy("gps");
    try {
      const g = await getGeo();
      setCoords(g);
      try {
        const { data } = await api.get(`/geo/reverse`, { params: { lat: g.lat, lng: g.lng } });
        if (data.address) setF((prev) => ({ ...prev, address: data.address }));
        if (data.cep) setCep(data.cep.replace(/\D/g, ""));
        toast.success("Localização e endereço preenchidos.");
      } catch { toast.success("Localização capturada (não foi possível preencher o endereço automaticamente)."); }
    } catch { toast.error("Não foi possível obter a localização."); }
    finally { setGeoBusy(""); }
  };
  const lookupCep = async () => {
    const d = (cep || "").replace(/\D/g, "");
    if (d.length !== 8) return toast.error("Informe um CEP com 8 dígitos.");
    setGeoBusy("cep");
    try {
      const { data } = await api.get(`/geo/cep/${d}`);
      setF((prev) => ({ ...prev, address: data.address }));
      toast.success("Endereço preenchido pelo CEP. Adicione o número e complemento.");
      try { const g = await api.get(`/geo/geocode`, { params: { q: data.address } }); setCoords({ lat: g.data.lat, lng: g.data.lng }); } catch {}
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "CEP não encontrado."); }
    finally { setGeoBusy(""); }
  };
  const geocodeAddress = async () => {
    if (!f.address.trim()) return toast.error("Digite o endereço primeiro.");
    setGeoBusy("geo");
    try {
      const { data } = await api.get(`/geo/geocode`, { params: { q: f.address } });
      setCoords({ lat: data.lat, lng: data.lng });
      toast.success("Endereço localizado no mapa.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Endereço não encontrado no mapa."); }
    finally { setGeoBusy(""); }
  };
  const onAddressChange = (e) => {
    const v = e.target.value;
    setF((prev) => ({ ...prev, address: v }));
    clearTimeout(sugTimer.current);
    if (v.trim().length < 4) { setSuggestions([]); return; }
    sugTimer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/geo/autocomplete`, { params: { q: v } }); setSuggestions(data || []); }
      catch { setSuggestions([]); }
    }, 450);
  };
  const pickSuggestion = (s) => {
    setF((prev) => ({ ...prev, address: s.address }));
    setCoords({ lat: s.lat, lng: s.lng });
    setSuggestions([]);
  };

  const validateStep = (s) => {
    if (s === 0) {
      if (!f.name.trim()) return "Informe o nome do evento.";
      if (!date) return "Selecione a data do evento.";
      if (!f.start_time || !f.end_time) return "Informe os horários de início e término.";
      if (!f.address.trim()) return "Informe o endereço do evento.";
      return null;
    }
    if (s === 1) {
      const clean = roles.filter((r) => r.instrument && (parseInt(r.quantity) || 0) >= 1);
      if (clean.length === 0) return "Adicione ao menos uma vaga (instrumento + cachê).";
      if (hasCacheErrors) return "Corrija os cachês destacados antes de continuar.";
      return null;
    }
    return null;
  };
  const goNext = () => {
    const err = validateStep(step);
    if (err) return toast.error(err);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async (e) => {
    e.preventDefault();
    if (step < STEPS.length - 1) return goNext();
    if (!date) return toast.error("Selecione a data do evento.");
    if (hasCacheErrors) return toast.error("Corrija os cachês destacados antes de publicar o evento.");
    const hasRoles = roles.some((r) => r.instrument && (parseInt(r.quantity) || 0) >= 1);
    if (!hasRoles) { setStep(1); return toast.error("Adicione ao menos uma vaga (instrumento + cachê)."); }
    if (!editMode) { setTplName(f.name || ""); return setSaveTplOpen(true); }
    await doPublish(false);
  };

  const doPublish = async (saveAsTemplate) => {
    setSaveTplOpen(false);
    if (saveAsTemplate && !editMode) {
      try { await saveTemplate(tplName); } catch { toast.error("Não foi possível salvar o modelo, mas o evento será publicado."); }
    }
    setLoading(true);
    try {
      const cleanRoles = roles
        .filter((r) => r.instrument && (parseInt(r.quantity) || 0) >= 1)
        .map((r) => ({ instrument: r.instrument, quantity: parseInt(r.quantity) || 1, cache: parseFloat(r.cache) || 0, min_level: r.min_level || null }));
      if (cleanRoles.length === 0) { setLoading(false); setStep(1); return toast.error("Adicione ao menos uma vaga (instrumento + cachê)."); }
      const payload = {
        ...f, type: f.type || null, image: f.image || null, apply_open: f.apply_open || null, apply_deadline: f.apply_deadline || null, genres, date: format(date, "yyyy-MM-dd"), end_date: format(endDate || date, "yyyy-MM-dd"), roles: cleanRoles,
        confirm_deadline_min: parseInt(f.confirm_deadline_min) || 60,
        invite_deadline_min: parseInt(f.invite_deadline_min) || 1440, repertoire,
        checkin_radius_m: parseInt(f.checkin_radius_m) || 500, checkin_lead_min: parseInt(f.checkin_lead_min) || 0,
        sound_check_lead_min: parseInt(f.sound_check_lead_min) || 0, sound_check_time: f.sound_check_time || null,
        lat: coords?.lat ?? null, lng: coords?.lng ?? null,
        ...(!editMode && recurrence.freq !== "none" && (parseInt(recurrence.count) || 0) > 1 ? { recurrence: { freq: recurrence.freq, count: Math.min(12, Math.max(2, parseInt(recurrence.count) || 2)) } } : {}),
      };
      if (editMode) {
        const { data } = await api.put(`/events/${id}`, payload);
        const changed = (data?.changes || []).length;
        toast.success(changed ? "Evento atualizado! Candidatos foram notificados." : "Evento atualizado!");
        navigate(`/eventos/${id}`);
      } else {
        const { data } = await api.post("/events", payload);
        let invited = 0;
        for (const inv of selectedInvites) {
          try { await api.post(`/events/${data.id}/invite`, { user_id: inv.user_id }); invited++; } catch (e) { /* skip incompatible */ }
        }
        toast.success((data.recurring_created > 1 ? `${data.recurring_created} eventos criados na recorrência!` : "Evento criado!") + (invited ? ` ${invited} convite(s) enviado(s) à sua equipe.` : " Músicos compatíveis foram notificados."));
        navigate(`/eventos/${data.id}`);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "plan_limit_reached") { setLimitInfo(d); }
      else if (d && typeof d === "object" && d.code === "event_validation") { toast.error(d.errors?.[0]?.message || d.message); }
      else { toast.error(apiError(d)); }
    }
    finally { setLoading(false); }
  };

  const inputCls = "mt-1.5 bg-[#09090B] border-white/10 rounded-xl";

  useEffect(() => {
    if (editMode) return;
    api.get("/event-templates").then((r) => { setTemplates(r.data || []); if ((r.data || []).length) setTplChoiceOpen(true); }).catch(() => {});
  }, [editMode]);
  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid); if (!t) return;
    const d = t.data || {};
    setF((p) => ({ ...p, name: d.name || "", type: d.type || "", image: d.image || "", address: d.address || "", requirements: d.requirements || "", notes: d.notes || "",
      confirm_deadline_min: d.confirm_deadline_min ?? 60, checkin_radius_m: d.checkin_radius_m ?? 500, checkin_lead_min: d.checkin_lead_min ?? 60,
      invite_deadline_min: d.invite_deadline_min ?? 1440, sound_check_enabled: !!d.sound_check_enabled, sound_check_time: d.sound_check_time || "", sound_check_lead_min: d.sound_check_lead_min ?? 0 }));
    if (Array.isArray(d.roles) && d.roles.length) setRoles(d.roles.map((r) => ({ instrument: r.instrument, quantity: r.quantity || 1, cache: r.cache ?? "", min_level: r.min_level || "" })));
    if (Array.isArray(d.genres)) setGenres(d.genres);
    if (Array.isArray(d.repertoire)) { setRepertoire(d.repertoire); setRepMode(d.repertoire.length ? "now" : "later"); }
    if (d.lat != null && d.lng != null) setCoords({ lat: d.lat, lng: d.lng });
    toast.success(`Modelo "${t.name}" aplicado. Escolha a data e publique.`);
  };
  const saveTemplate = async (name) => {
    const nm = (name || "").trim();
    if (!nm) throw new Error("template name required");
    await api.post("/event-templates", { name: nm, data: {
      name: f.name, type: f.type, genres, image: f.image, address: f.address, lat: coords?.lat ?? null, lng: coords?.lng ?? null,
      requirements: f.requirements, notes: f.notes, confirm_deadline_min: f.confirm_deadline_min, checkin_radius_m: f.checkin_radius_m,
      checkin_lead_min: f.checkin_lead_min, invite_deadline_min: f.invite_deadline_min, sound_check_enabled: f.sound_check_enabled,
      sound_check_time: f.sound_check_time, sound_check_lead_min: f.sound_check_lead_min,
      roles: roles.filter((r) => r.instrument), repertoire } });
    toast.success("Modelo salvo!");
    api.get("/event-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  };
  const cardCls = "rounded-2xl border border-white/10 bg-card p-5 space-y-4";

  if (!ready) return <div className="max-w-3xl mx-auto py-20 text-center text-muted-foreground" data-testid="edit-loading">Carregando evento…</div>;
  if (limitInfo) return (
    <div className="max-w-2xl mx-auto py-16" data-testid="plan-limit-reached">
      <div className="bg-card border border-[#FF4D00]/30 rounded-2xl p-8 space-y-5">
        <div className="text-center space-y-2">
          <AlertTriangle className="h-10 w-10 text-[#FF4D00] mx-auto" />
          <h1 className="font-head text-2xl font-bold">{limitInfo.message}</h1>
          <p className="text-muted-foreground">Plano <span className="text-foreground font-semibold">{limitInfo.plan?.name}</span> · você usou {limitInfo.used} de {limitInfo.limit} eventos neste ciclo.</p>
        </div>
        {(limitInfo.upgrade_options || []).length > 0 && (
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Faça upgrade e crie mais eventos</div>
            {limitInfo.upgrade_options.map((p) => (
              <div key={p.slug} className="flex items-center justify-between bg-[#09090B] border border-white/10 rounded-xl px-4 py-3">
                <div><span className="font-semibold">{p.name}</span> <span className="text-muted-foreground text-sm">· {p.event_limit} eventos/mês</span></div>
                <div className="font-bold">{money(p.price)}<span className="text-xs text-muted-foreground font-normal">/mês</span></div>
              </div>
            ))}
          </div>
        )}
        <div className="border-t border-white/10 pt-4 space-y-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground text-center">Ou crie um evento avulso agora, via Pix</div>
          <ExtraEventPix fee={limitInfo.event_extra_fee} onCredited={() => { toast.success("Evento avulso liberado! Preencha e publique."); setLimitInfo(null); }} />
        </div>
        <div className="flex gap-2 justify-center pt-1">
          <Button variant="outline" onClick={() => setLimitInfo(null)} className="rounded-xl bg-transparent border-white/10">Voltar</Button>
          <Button data-testid="limit-see-plans-btn" onClick={() => navigate("/meu-plano")} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500]">Ver planos</Button>
        </div>
      </div>
    </div>
  );
  if (editMode && blocked) return (
    <div className="max-w-2xl mx-auto py-16" data-testid="edit-blocked">
      <div className="bg-card border border-amber-500/20 rounded-2xl p-8 text-center space-y-4">
        <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto" />
        <h1 className="font-head text-2xl font-bold">Edição indisponível</h1>
        <p className="text-muted-foreground">{blocked}</p>
        <Button onClick={() => navigate(`/eventos/${id}`)} className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500]">Voltar ao evento</Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto" data-testid={editMode ? "edit-event-page" : "create-event-page"}>
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">{editMode ? "Editar evento" : "Criar evento"}</h1>
      <p className="text-muted-foreground mb-6">{editMode ? "Atualize os detalhes. Os candidatos serão notificados." : "Monte sua oportunidade em 4 passos e veja a prévia ao vivo."}</p>

      {editMode && hasConfirmed && (
        <div className="mb-6 flex items-start gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-2xl p-3" data-testid="confirmed-lock-banner">
          <Lock className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Este evento já tem candidato confirmado. Cachê, instrumento e grau de especialidade ficam bloqueados.</span>
        </div>
      )}

      <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-6 lg:items-start">
        {/* Prévia (mobile: topo) */}
        <div className="lg:hidden mb-5"><EventPreview {...{ f, date, endDate, instrList, totalSlots, rolesTotal, editMode, genres }} /></div>

        <div className="min-w-0">
          {/* Stepper */}
          <div className="mb-5" data-testid="wizard-stepper">
            <div className="flex items-center gap-2">
              {STEPS.map((label, i) => (
                <div key={i} className="flex-1 flex items-center gap-2">
                  <div className={`flex items-center gap-2 min-w-0 ${i <= step ? "" : "opacity-50"}`} aria-current={i === step ? "step" : undefined}>
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold border ${i < step ? "bg-accent border-accent text-zinc-950" : i === step ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "border-white/15 text-muted-foreground"}`}>
                      {i < step ? <Check className="h-4 w-4" /> : i + 1}
                    </div>
                    <span className={`text-xs font-semibold truncate hidden sm:block ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded-full ${i < step ? "bg-accent" : "bg-white/10"}`} />}
                </div>
              ))}
            </div>
            <div className="sm:hidden mt-2 text-sm font-semibold text-foreground">Etapa {step + 1} de {STEPS.length} · {STEPS[step]}</div>
          </div>

          <form onSubmit={submit} className="space-y-5">
            {/* ETAPA 1 — DADOS BÁSICOS */}
            {step === 0 && (
              <div className="space-y-5" data-testid="wizard-step-0">
                <div className={cardCls}>
                  <div><Label className="text-muted-foreground">Nome do evento *</Label>
                    <Input data-testid="ev-name" required value={f.name} onChange={set("name")} placeholder="Ex: Roda de samba no Bar do Zé" className={inputCls} /></div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label className="text-muted-foreground">Data de início *</Label>
                      <Popover open={dateOpen} onOpenChange={setDateOpen}>
                        <PopoverTrigger asChild>
                          <button type="button" data-testid="ev-date" className="mt-1.5 w-full flex items-center gap-2 px-3 py-2.5 bg-[#09090B] border border-white/10 rounded-xl text-sm text-left hover:border-white/20 transition-colors">
                            <CalendarIcon className="h-4 w-4 text-muted-foreground" />{date ? format(date, "dd/MM/yyyy") : <span className="text-muted-foreground">Selecionar</span>}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#121217] border-white/10"><Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); if (d && (!endDate || endDate < d)) setEndDate(d); setDateOpen(false); }} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} /></PopoverContent>
                      </Popover>
                    </div>
                    <div><Label className="text-muted-foreground">Início *</Label><Input data-testid="ev-start" required type="time" value={f.start_time} onChange={set("start_time")} className={inputCls} /></div>
                    <div><Label className="text-muted-foreground">Data de término *</Label>
                      <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                        <PopoverTrigger asChild>
                          <button type="button" data-testid="ev-end-date" className="mt-1.5 w-full flex items-center gap-2 px-3 py-2.5 bg-[#09090B] border border-white/10 rounded-xl text-sm text-left hover:border-white/20 transition-colors">
                            <CalendarIcon className="h-4 w-4 text-muted-foreground" />{(endDate || date) ? format(endDate || date, "dd/MM/yyyy") : <span className="text-muted-foreground">Selecionar</span>}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#121217] border-white/10"><Calendar mode="single" selected={endDate || date} onSelect={(d) => { setEndDate(d); setEndDateOpen(false); }} disabled={(d) => date && d < new Date(date.getFullYear(), date.getMonth(), date.getDate())} /></PopoverContent>
                      </Popover>
                    </div>
                    <div><Label className="text-muted-foreground">Término *</Label><Input data-testid="ev-end" required type="time" value={f.end_time} onChange={set("end_time")} className={inputCls} /></div>
                  </div>
                  {date && endDate && format(endDate, "yyyy-MM-dd") !== format(date, "yyyy-MM-dd") && (
                    <p data-testid="crosses-midnight-hint" className="text-xs text-accent -mt-1">
                      {Math.round((new Date(format(endDate, "yyyy-MM-dd")) - new Date(format(date, "yyyy-MM-dd"))) / 86400000) === 1
                        ? `Este evento vira o dia — termina na madrugada de ${format(endDate, "dd/MM/yyyy")}.`
                        : `Evento de vários dias — termina em ${format(endDate, "dd/MM/yyyy")}.`}
                    </p>
                  )}
                </div>

                <div className={cardCls} data-testid="type-genre-section">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Tipo do evento</Label>
                      <Select value={f.type || ""} onValueChange={(v) => setF({ ...f, type: v })}>
                        <SelectTrigger data-testid="ev-type" className={inputCls}><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                        <SelectContent className="bg-[#121217] border-white/10">{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Imagem de capa</Label>
                      <div className="mt-1.5 flex items-center gap-3">
                        <label data-testid="ev-image-upload" className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-xl border border-white/10 bg-[#09090B] text-sm text-zinc-300 hover:border-accent/50 transition-colors">
                          <ImageIcon className="h-4 w-4 text-accent" />{f.image ? "Trocar imagem" : "Enviar imagem"}
                          <input type="file" accept="image/*" className="hidden" onChange={onCover} />
                        </label>
                        {f.image && (
                          <div className="relative">
                            <img src={f.image} alt="capa" className="h-12 w-16 object-cover rounded-lg border border-white/10" />
                            <button type="button" data-testid="ev-image-remove" onClick={() => setF((p) => ({ ...p, image: "" }))} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-black border border-white/20 text-zinc-300 flex items-center justify-center"><X className="h-3 w-3" /></button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Gêneros musicais</Label>
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="ev-genres">
                      {GENRES.map((g) => {
                        const on = genres.includes(g);
                        return (
                          <button key={g} type="button" data-testid={`genre-${g}`} onClick={() => toggleGenre(g)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${on ? "bg-accent/15 border-accent/50 text-accent" : "border-white/10 text-muted-foreground hover:border-white/25"}`}>{g}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="border-t border-white/[0.06] pt-4">
                    <Label className="text-muted-foreground flex items-center gap-1.5"><Clock className="h-4 w-4 text-accent" />Janela de candidaturas (opcional)</Label>
                    <p className="text-xs text-muted-foreground mt-1">Defina quando as candidaturas abrem e/ou encerram — os músicos verão uma contagem regressiva no card.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label className="text-xs text-muted-foreground">Abrem em</Label>
                        <Input data-testid="ev-apply-open" type="datetime-local" value={f.apply_open} onChange={set("apply_open")} className={inputCls} />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Encerram em</Label>
                        <Input data-testid="ev-apply-deadline" type="datetime-local" value={f.apply_deadline} onChange={set("apply_deadline")} className={inputCls} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={cardCls} data-testid="location-section">
                  <div>
                    <Label className="text-foreground flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#FF4D00]" />Local do evento</Label>
                    <p className="text-xs text-muted-foreground mt-1">Use o CEP, digite o endereço, ou use sua localização. As coordenadas alimentam o mapa e o cálculo de distância.</p>
                  </div>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[140px]">
                      <Label className="text-muted-foreground text-xs">CEP</Label>
                      <Input data-testid="ev-cep" value={cep} onChange={(e) => setCep(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupCep(); } }} placeholder="00000-000" inputMode="numeric" className={inputCls} />
                    </div>
                    <Button type="button" data-testid="ev-cep-btn" onClick={lookupCep} disabled={geoBusy === "cep"} variant="outline" className="rounded-xl gap-1.5 bg-transparent border-white/10 h-10">
                      {geoBusy === "cep" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Buscar CEP
                    </Button>
                    <Button type="button" data-testid="ev-geo-btn" onClick={useMyLocation} disabled={geoBusy === "gps"} variant="outline" className="rounded-xl gap-1.5 bg-transparent border-accent/30 text-accent h-10">
                      {geoBusy === "gps" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}Usar minha localização
                    </Button>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Endereço completo *</Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <Input data-testid="ev-address" required value={f.address} onChange={onAddressChange} onBlur={() => setTimeout(() => setSuggestions([]), 250)} autoComplete="off" placeholder="Rua, número, bairro, cidade - UF" className={`${inputCls} w-full`} />
                        {suggestions.length > 0 && (
                          <ul data-testid="address-suggestions" className="absolute z-20 left-0 right-0 mt-1 bg-[#121217] border border-white/10 rounded-xl shadow-xl max-h-72 overflow-auto overscroll-contain">
                            {suggestions.map((s, i) => (
                              <li key={i}>
                                <button type="button" data-testid={`address-suggestion-${i}`} onPointerDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                                  className="w-full text-left px-4 py-3 text-sm text-zinc-300 hover:bg-[#1C1C24] active:bg-[#1C1C24] flex items-start gap-2 leading-snug border-b border-white/10 last:border-0">
                                  <MapPin className="h-4 w-4 text-[#FF4D00] mt-0.5 shrink-0" /><span className="break-words">{s.display_name}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <Button type="button" data-testid="ev-geocode-btn" onClick={geocodeAddress} disabled={geoBusy === "geo"} variant="outline" className="rounded-xl gap-1.5 bg-transparent border-white/10 sm:mt-1.5 h-11 sm:h-10 w-full sm:w-auto shrink-0" title="Localizar este endereço no mapa">
                        {geoBusy === "geo" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}Localizar no mapa
                      </Button>
                    </div>
                    <p className="mt-1.5 text-xs" data-testid="ev-coords-status">
                      {coords ? <span className="text-accent flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Coordenadas definidas ({coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}) — mapa e distância ativos.</span>
                        : <span className="text-amber-400">Sem coordenadas ainda. Busque o CEP, use sua localização, digite o endereço ou toque no mapa.</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1.5">Confira e ajuste: toque no mapa ou arraste o pino para a posição exata do palco.</p>
                    <MapPicker coords={coords} onChange={setCoords} />
                  </div>
                </div>
              </div>
            )}

            {/* ETAPA 2 — VAGAS & CACHÊS */}
            {step === 1 && (
              <div className="space-y-5" data-testid="wizard-step-1">
                <div className={`${cardCls}`} data-testid="roles-section">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground flex items-center gap-1">Vagas & cachês por instrumento{hasConfirmed && <Lock className="h-3 w-3 text-amber-400" />}</Label>
                    {!hasConfirmed && <Button type="button" data-testid="add-role-btn" onClick={addRole} variant="outline" size="sm" className="rounded-xl bg-transparent border-white/10 gap-1 h-8"><Plus className="h-3.5 w-3.5" />Adicionar vaga</Button>}
                  </div>
                  <p className="text-xs text-muted-foreground -mt-1">Cada vaga tem instrumento, quantidade e cachê por músico. O músico se candidata à vaga compatível com o perfil dele.</p>
                  {hasConfirmed && <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-xl p-2"><Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />Há candidato confirmado — as vagas ficam bloqueadas.</div>}
                  <div className="hidden md:grid grid-cols-[1fr_80px_140px_150px_36px] gap-2 text-[10px] uppercase tracking-widest text-muted-foreground px-1">
                    <span>Instrumento</span><span>Qtd</span><span>Cachê/músico</span><span>Nível mín.</span><span></span>
                  </div>
                  {roles.map((r, idx) => (
                    <div key={idx} data-testid={`role-row-${idx}`} className="grid grid-cols-2 md:grid-cols-[1fr_80px_140px_150px_36px] gap-2 items-center">
                      <Select value={r.instrument} onValueChange={(v) => setRole(idx, "instrument", v)} disabled={hasConfirmed}>
                        <SelectTrigger data-testid={`role-instrument-${idx}`} className="bg-[#09090B] border-white/10 rounded-xl h-9"><SelectValue placeholder="Instrumento" /></SelectTrigger>
                        <SelectContent className="bg-[#121217] border-white/10">{INSTRUMENTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input data-testid={`role-qty-${idx}`} type="number" min="1" value={r.quantity} onChange={(e) => setRole(idx, "quantity", e.target.value)} disabled={hasConfirmed} className="bg-[#09090B] border-white/10 rounded-xl h-9" />
                      <div>
                        <Input data-testid={`role-cache-${idx}`} type="number" min="0" step="0.01" value={r.cache} onChange={(e) => setRole(idx, "cache", e.target.value)} disabled={hasConfirmed} placeholder="0,00" className={`bg-[#09090B] rounded-xl h-9 ${r.instrument && roleCacheError(r) ? "border-red-500 focus-visible:ring-red-500" : "border-white/10"}`} />
                        {r.instrument && minCache[r.instrument] != null && <span className="text-[10px] text-muted-foreground block mt-0.5" data-testid={`role-cache-min-${idx}`}>Mínimo: {money(minCache[r.instrument])}</span>}
                        {r.instrument && !hasConfirmed && (
                          <button type="button" data-testid={`role-suggest-cache-${idx}`} onClick={() => suggestCache(idx)} className="text-[10px] text-primary hover:underline inline-flex items-center gap-1 mt-0.5"><Wand2 className="h-3 w-3" />Cachê inteligente</button>
                        )}
                        {r.instrument && cacheSug[r.instrument] && (
                          <span data-testid={`role-cache-sug-${idx}`} className="block text-[10px] text-accent mt-0.5">Sugestão Mí Chamou: {money(cacheSug[r.instrument].suggested)} {cacheSug[r.instrument].sample_size > 0 ? `· faixa ${money(cacheSug[r.instrument].range.low)}–${money(cacheSug[r.instrument].range.high)}` : "· sem histórico ainda"}</span>
                        )}
                        {r.instrument && cacheSug[r.instrument] && parseFloat(r.cache) > 0 && parseFloat(r.cache) < cacheSug[r.instrument].warn_below && (
                          <span data-testid={`role-cache-warn-${idx}`} className="block text-[10px] text-amber-400 mt-0.5">Este valor pode reduzir a quantidade de músicos disponíveis.</span>
                        )}
                        {r.instrument && roleCacheError(r) && <span data-testid={`role-cache-error-${idx}`} className="text-[10px] text-red-400 block mt-0.5 leading-tight">{roleCacheError(r)}</span>}
                      </div>
                      <Select value={r.min_level || "any"} onValueChange={(v) => setRole(idx, "min_level", v === "any" ? "" : v)} disabled={hasConfirmed}>
                        <SelectTrigger data-testid={`role-level-${idx}`} className="bg-[#09090B] border-white/10 rounded-xl h-9"><SelectValue placeholder="Opcional" /></SelectTrigger>
                        <SelectContent className="bg-[#121217] border-white/10"><SelectItem value="any">Qualquer nível</SelectItem>{LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                      </Select>
                      {!hasConfirmed && roles.length > 1
                        ? <button type="button" data-testid={`role-remove-${idx}`} onClick={() => removeRole(idx)} className="text-destructive hover:text-destructive flex justify-center"><Trash2 className="h-4 w-4" /></button>
                        : <span />}
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 border-t border-white/10">
                    <span className="text-sm text-muted-foreground">Total dos cachês</span>
                    <span data-testid="roles-total" className="font-mono text-accent font-bold text-lg">{money(rolesTotal)}</span>
                  </div>
                </div>

                <div className={cardCls} data-testid="team-builder-section">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <Label className="text-foreground flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" />Montar minha equipe</Label>
                      <p className="text-xs text-muted-foreground mt-1">Encontre músicos compatíveis (Match) ou chame sua equipe salva. Eles recebem o convite assim que você publicar.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" data-testid="invite-primary-team" variant="outline" onClick={inviteMyTeam} className="rounded-xl bg-transparent border-primary/30 text-primary gap-1.5 h-9"><Heart className="h-4 w-4" />Convidar equipe principal</Button>
                      <Button type="button" data-testid="open-team-builder" variant="outline" onClick={() => setTeamOpen(true)} className="rounded-xl bg-transparent border-primary/30 text-primary gap-1.5 h-9"><Users className="h-4 w-4" />Montar / Chamar equipe</Button>
                    </div>
                  </div>
                  {selectedInvites.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1" data-testid="selected-invites">
                      {selectedInvites.map((s) => (
                        <span key={s.user_id} className="inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary border border-primary/30 rounded-full pl-3 pr-1.5 py-1">
                          {s.stage_name || "Músico"}
                          <button type="button" data-testid={`remove-invite-${s.user_id}`} onClick={() => setSelectedInvites((p) => p.filter((x) => x.user_id !== s.user_id))} className="h-4 w-4 flex items-center justify-center rounded-full hover:bg-primary/20"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <TeamBuilder open={teamOpen} onOpenChange={setTeamOpen} preselected={selectedInvites}
                    context={{ date: date ? format(date, "yyyy-MM-dd") : null, start_time: f.start_time || null, end_time: f.end_time || null, lat: coords?.lat ?? null, lng: coords?.lng ?? null, roles: roles.filter((r) => r.instrument).map((r) => ({ instrument: r.instrument, quantity: parseInt(r.quantity) || 1, cache: parseFloat(r.cache) || 0, min_level: r.min_level || null })) }}
                    onConfirm={(list) => setSelectedInvites(list)} />
                </div>

                <div className={cardCls} data-testid="payment-mode-section">
                  <Label className="text-foreground flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-primary" />Como deseja pagar os músicos deste evento?</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <button type="button" data-testid="payment-mode-protected" onClick={() => setF({ ...f, payment_mode: "protected" })} aria-pressed={f.payment_mode === "protected"}
                      className={`text-left rounded-2xl border p-4 transition-colors ${f.payment_mode === "protected" ? "border-primary bg-primary/[0.07] ring-1 ring-primary/40" : "border-white/10 bg-[#09090B] hover:border-white/25"}`}>
                      <div className="flex items-center gap-2 font-semibold"><ShieldCheck className={`h-4 w-4 ${f.payment_mode === "protected" ? "text-primary" : "text-muted-foreground"}`} />Pagamento Protegido <span className="text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded">Recomendado</span>{f.payment_mode === "protected" && <Check className="h-4 w-4 text-primary ml-auto" />}</div>
                      <p className="text-xs text-muted-foreground mt-1">Você paga antecipado; o valor fica <span className="text-foreground">garantido</span> e é liberado ao músico <span className="text-foreground">após a conclusão</span> do evento. Mais confiança e mais músicos interessados.</p>
                    </button>
                    <button type="button" data-testid="payment-mode-flexible" onClick={() => setF({ ...f, payment_mode: "flexible" })} aria-pressed={f.payment_mode === "flexible"}
                      className={`text-left rounded-2xl border p-4 transition-colors ${f.payment_mode === "flexible" ? "border-accent bg-accent/[0.06] ring-1 ring-accent/40" : "border-white/10 bg-[#09090B] hover:border-white/25"}`}>
                      <div className="flex items-center gap-2 font-semibold"><Wallet className={`h-4 w-4 ${f.payment_mode === "flexible" ? "text-accent" : "text-muted-foreground"}`} />Pagamento Flexível{f.payment_mode === "flexible" && <Check className="h-4 w-4 text-accent ml-auto" />}</div>
                      <p className="text-xs text-muted-foreground mt-1">Você paga quando preferir: antes, no início, durante ou ao final do evento.</p>
                    </button>
                  </div>
                </div>

                <div className={cardCls}>
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground">Passagem de som</Label>
                    <Switch data-testid="ev-soundcheck" checked={f.sound_check_enabled} onCheckedChange={(v) => setF({ ...f, sound_check_enabled: v })} />
                  </div>
                  {f.sound_check_enabled && (
                    <div className="grid md:grid-cols-2 gap-4">
                      <div><Label className="text-muted-foreground text-xs">Horário da passagem de som</Label><Input data-testid="ev-sc-time" type="time" value={f.sound_check_time} onChange={set("sound_check_time")} className={inputCls} /></div>
                      <div><Label className="text-muted-foreground text-xs">Ou chegar X min antes do show</Label><Input data-testid="ev-sc-lead" type="number" min="0" value={f.sound_check_lead_min} onChange={set("sound_check_lead_min")} className={inputCls} /></div>
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* ETAPA 3 — REPERTÓRIO */}
            {step === 2 && (
              <div className="space-y-5" data-testid="wizard-step-2">
                <div className={cardCls} data-testid="repertoire-section">
                  <div>
                    <Label className="text-foreground flex items-center gap-1.5"><Music className="h-4 w-4 text-[#FF4D00]" />Repertório do evento</Label>
                    <p className="text-xs text-muted-foreground mt-1">O repertório <span className="text-accent font-semibold">não é obrigatório agora</span> — você pode montar depois, dentro do evento. A letra/cifra e os arquivos ficam liberados para o músico somente <span className="text-accent">após ele confirmar</span> a participação.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button type="button" data-testid="rep-mode-now" onClick={() => setRepMode("now")} aria-pressed={repMode === "now"}
                      className={`text-left rounded-2xl border p-4 transition-colors ${repMode === "now" ? "border-accent bg-accent/[0.06]" : "border-white/10 bg-[#09090B] hover:border-white/25"}`}>
                      <div className="flex items-center gap-2 font-semibold"><ListMusic className={`h-4 w-4 ${repMode === "now" ? "text-accent" : "text-muted-foreground"}`} />Definir agora{repMode === "now" && <Check className="h-4 w-4 text-accent ml-auto" />}</div>
                      <p className="text-xs text-muted-foreground mt-1">Escolha músicas da Biblioteca, use um setlist, ou importe/adicione — e organize a ordem.</p>
                    </button>
                    <button type="button" data-testid="rep-mode-later" onClick={() => setRepMode("later")} aria-pressed={repMode === "later"}
                      className={`text-left rounded-2xl border p-4 transition-colors ${repMode === "later" ? "border-accent bg-accent/[0.06]" : "border-white/10 bg-[#09090B] hover:border-white/25"}`}>
                      <div className="flex items-center gap-2 font-semibold"><Clock className={`h-4 w-4 ${repMode === "later" ? "text-accent" : "text-muted-foreground"}`} />Definir depois{repMode === "later" && <Check className="h-4 w-4 text-accent ml-auto" />}</div>
                      <p className="text-xs text-muted-foreground mt-1">Avance sem músicas. Configure o repertório a qualquer momento na página do evento.</p>
                    </button>
                  </div>
                  {repMode === "now" ? (
                    <div className="border-t border-white/[0.06] pt-4" data-testid="rep-now-panel">
                      <EventRepertoire songs={repertoire} onChange={setRepertoire} />
                    </div>
                  ) : (
                    <div className="border-t border-white/[0.06] pt-4 flex items-start gap-2 text-sm text-muted-foreground" data-testid="rep-later-note">
                      <Clock className="h-4 w-4 text-accent shrink-0 mt-0.5" /><span>Tudo certo! Você poderá adicionar músicas, setlists e arquivos depois, na página do evento.</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ETAPA 4 — REGRAS & PUBLICAÇÃO */}
            {step === 3 && (
              <div className="space-y-5" data-testid="wizard-step-3">
                {!editMode && (
                  <div className={cardCls} data-testid="recurrence-card">
                    <div className="flex items-center gap-2"><Repeat className="h-5 w-5 text-accent" /><Label className="text-foreground">Repetir evento</Label></div>
                    <p className="text-xs text-muted-foreground -mt-1">Crie vários eventos independentes de uma vez (máx. 12). Cada ocorrência conta no seu limite de plano.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-muted-foreground text-xs">Frequência</Label>
                        <Select value={recurrence.freq} onValueChange={(v) => setRecurrence((p) => ({ ...p, freq: v }))}>
                          <SelectTrigger data-testid="recurrence-freq" className={inputCls}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#121217] border-white/10">
                            <SelectItem value="none">Não repetir</SelectItem>
                            <SelectItem value="daily">Diariamente</SelectItem>
                            <SelectItem value="weekly">Semanalmente</SelectItem>
                            <SelectItem value="monthly">Mensalmente</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {recurrence.freq !== "none" && (
                        <div>
                          <Label className="text-muted-foreground text-xs">Nº de ocorrências (2–12)</Label>
                          <Input data-testid="recurrence-count" type="number" min="2" max="12" value={recurrence.count} onChange={(e) => setRecurrence((p) => ({ ...p, count: e.target.value }))} className={inputCls} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className={cardCls}>
                  <Label className="text-foreground">Regras de confirmação & check-in</Label>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><Label className="text-muted-foreground text-xs">Prazo p/ confirmar após aprovação (min)</Label><Input data-testid="ev-deadline" type="number" min="5" value={f.confirm_deadline_min} onChange={set("confirm_deadline_min")} className={inputCls} /></div>
                    <div><Label className="text-muted-foreground text-xs">Raio permitido p/ check-in (m)</Label><Input data-testid="ev-radius" type="number" min="50" value={f.checkin_radius_m} onChange={set("checkin_radius_m")} className={inputCls} /></div>
                    <div><Label className="text-muted-foreground text-xs">Liberar check-in quanto tempo antes (min)</Label><Input data-testid="ev-checkin-lead" type="number" min="0" value={f.checkin_lead_min} onChange={set("checkin_lead_min")} className={inputCls} /><p className="text-[11px] text-muted-foreground/70 mt-1">Antes da chegada obrigatória{f.sound_check_enabled ? " / passagem de som" : ""}.</p></div>
                    <div><Label className="text-muted-foreground text-xs">Validade do convite direto (min)</Label><Input data-testid="ev-invite-deadline" type="number" min="15" value={f.invite_deadline_min} onChange={set("invite_deadline_min")} className={inputCls} /></div>
                  </div>
                </div>
                <div className={cardCls}>
                  <div><Label className="text-muted-foreground">Requisitos para participação</Label><Textarea data-testid="ev-requirements" value={f.requirements} onChange={set("requirements")} placeholder="Ex: repertório de MPB, equipamento próprio..." className={inputCls} /></div>
                  <div><Label className="text-muted-foreground">Observações</Label><Textarea data-testid="ev-notes" value={f.notes} onChange={set("notes")} placeholder="Informações adicionais" className={inputCls} /></div>
                </div>
              </div>
            )}

            {/* Navegação do assistente */}
            <div className="flex gap-3 pt-1">
              {step > 0 && <Button type="button" data-testid="wizard-back-btn" onClick={goBack} variant="outline" className="rounded-xl bg-transparent border-white/10 gap-1.5"><ChevronLeft className="h-4 w-4" />Voltar</Button>}
              {step < STEPS.length - 1
                ? <Button key="wizard-next" type="button" data-testid="wizard-next-btn" onClick={goNext} className="bg-[#FF4D00] hover:bg-[#E64500] text-white rounded-xl font-semibold flex-1 gap-1.5">Avançar<ChevronRight className="h-4 w-4" /></Button>
                : <Button key="wizard-submit" data-testid="submit-event-btn" disabled={loading || hasCacheErrors} type="submit" className="bg-gradient-to-r from-[#FF4D00] to-accent text-white rounded-xl font-bold flex-1 shadow-[0_0_20px_rgba(255,79,0,0.35)]">{loading ? (editMode ? "Salvando..." : "Publicando...") : (editMode ? "Salvar alterações" : "Publicar evento")}</Button>}
              <Button type="button" variant="ghost" onClick={() => navigate(editMode ? `/eventos/${id}` : -1)} className="rounded-xl text-muted-foreground">Cancelar</Button>
            </div>
          </form>
        </div>

        {/* Prévia (desktop: direita, sticky) */}
        <div className="hidden lg:block sticky top-24"><EventPreview {...{ f, date, endDate, instrList, totalSlots, rolesTotal, editMode, genres }} /></div>
      </div>

      {/* Modal 1 — escolher criar a partir de modelo salvo (apenas se houver modelos) */}
      <Dialog open={tplChoiceOpen} onOpenChange={setTplChoiceOpen}>
        <DialogContent data-testid="template-choice-modal" className="bg-[#121217] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LayoutTemplate className="h-5 w-5 text-accent" />Criar a partir de um modelo salvo?</DialogTitle>
            <DialogDescription>Você tem modelos salvos. Comece a partir de um deles ou crie um evento do zero.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[46vh] overflow-y-auto -mx-1 px-1">
            {templates.map((t) => (
              <button key={t.id} type="button" data-testid={`tpl-choice-${t.id}`} onClick={() => { applyTemplate(t.id); setTplChoiceOpen(false); }}
                className="w-full text-left rounded-xl border border-white/10 bg-[#09090B] hover:border-accent/40 px-4 py-3 transition-colors flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="block font-semibold truncate">{t.name}</span>
                  <span className="block text-xs text-muted-foreground truncate">{t.data?.type || "Evento"}{(t.data?.roles || []).length ? ` · ${t.data.roles.length} vaga(s)` : ""}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-accent shrink-0" />
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" data-testid="tpl-choice-new-btn" variant="outline" onClick={() => setTplChoiceOpen(false)} className="w-full rounded-xl bg-transparent border-white/10 gap-1.5"><Plus className="h-4 w-4" />Criar novo evento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal 2 — salvar como modelo ao publicar (apenas criação) */}
      <Dialog open={saveTplOpen} onOpenChange={(o) => { if (!loading) setSaveTplOpen(o); }}>
        <DialogContent data-testid="save-template-modal" className="bg-[#121217] border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Save className="h-5 w-5 text-accent" />Salvar este evento como modelo?</DialogTitle>
            <DialogDescription>Reutilize as configurações (vagas, cachês, regras e repertório) em novas criações.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-muted-foreground text-sm">Nome do modelo</Label>
            <Input data-testid="save-template-name" value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder={f.name || "Ex: Show de sexta — banda completa"} className={inputCls} />
          </div>
          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button type="button" data-testid="publish-only-btn" variant="outline" disabled={loading} onClick={() => doPublish(false)} className="rounded-xl bg-transparent border-white/10 flex-1">Apenas publicar</Button>
            <Button type="button" data-testid="save-template-publish-btn" disabled={loading} onClick={() => { if (!tplName.trim()) return toast.error("Dê um nome ao modelo."); doPublish(true); }} className="bg-gradient-to-r from-[#FF4D00] to-accent text-white rounded-xl font-semibold flex-1 gap-1.5"><Save className="h-4 w-4" />Salvar como modelo e publicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const EventPreview = ({ f, date, endDate, instrList, totalSlots, rolesTotal, editMode, genres = [] }) => {
  const dateLabel = date ? format(date, "dd/MM/yyyy") : "Data a definir";
  const bg = f.image || PREVIEW_IMG;
  return (
    <div className="rounded-2xl overflow-hidden border border-accent/40 shadow-[0_0_28px_rgba(255,170,0,0.14)]" data-testid="event-preview">
      <div className="relative p-4 min-h-[190px] flex flex-col justify-between bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(to top right, rgba(15,15,18,0.94), rgba(15,15,18,0.55)), url(${bg})` }}>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#FF4D00] text-white">{f.type || "Evento"}</span>
          <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-white/20 bg-black/40 text-muted-foreground">{editMode ? "Edição" : "Prévia"}</span>
        </div>
        <div>
          <h3 className="font-head text-xl font-extrabold leading-tight" data-testid="preview-name">{f.name || "Nome do evento"}</h3>
          <div className="mt-2 space-y-1.5 text-sm">
            <div className="flex items-center gap-2 text-zinc-200"><CalendarIcon className="h-4 w-4 text-accent shrink-0" /><span className="truncate">{dateLabel} • {f.start_time || "--:--"} – {f.end_time || "--:--"}</span></div>
            <div className="flex items-center gap-2 text-zinc-200"><MapPin className="h-4 w-4 text-accent shrink-0" /><span className="truncate">{f.address || "Local do evento"}</span></div>
          </div>
          {genres.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="preview-genres">
              {genres.slice(0, 3).map((g) => <span key={g} className="text-[10px] px-2 py-0.5 rounded-full bg-black/40 border border-white/15 text-zinc-200">{g}</span>)}
            </div>
          )}
        </div>
      </div>
      <div className="bg-card p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Music className="h-4 w-4 text-[#FF4D00] shrink-0" />
          <span className="truncate text-muted-foreground">{instrList.length ? instrList.join(", ") : "Instrumentos das vagas"}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-muted-foreground"><Users className="h-4 w-4 text-accent" />{totalSlots} vaga{totalSlots === 1 ? "" : "s"}</span>
          <span className="font-mono font-bold text-accent">{money(rolesTotal)}</span>
        </div>
      </div>
    </div>
  );
};
